// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

import { useTheme } from '@azure/communication-react';
import { DefaultButton, IImageStyles, IStackStyles, Image, ITheme, Spinner, Stack } from '@fluentui/react';
import { canvasToBlob, takeScreenshot } from 'feedbacks/TakeScreenshot';
import { useEffect, useState } from 'react';

export interface FeedbackScreenshotProps {
  /** Called when the screenshot is ready or removed */
  onScreenshotResultChanged: (screenshot: Blob | null) => void;
  /** Called before taking a screenshot. Used to hide the feedback dialog before taking a screenshot */
  onBeforeScreenshot?: () => void;
  /** Called once the takeScreenshot API completes. Used to reshow the feedback dialog that was hidden for the screenshot. */
  onAfterScreenshot?: () => void;
  /** Called if the takeScreenshot API fails. */
  onScreenshotFailed?: () => void;
}

export const FeedbackScreenshot = (props: FeedbackScreenshotProps): JSX.Element => {
  const [processingScreenshot, setProcessingScreenshot] = useState(false);
  const [currentScreenshot, setCurrentScreenshot] = useState<HTMLCanvasElement>();
  const theme = useTheme();

  const onTakeScreenshot = async (): Promise<void> => {
    setProcessingScreenshot(true);
    try {
      await props.onBeforeScreenshot?.();
      const screenshotAsCanvas = await takeScreenshot();
      setCurrentScreenshot(screenshotAsCanvas);
      await props.onAfterScreenshot?.(); // call this before finishing processing so the dialog can be re-shown.
      const screenshotAsBlob = await canvasToBlob(screenshotAsCanvas);
      props.onScreenshotResultChanged(screenshotAsBlob);
    } catch (e) {
      alert('Failed to take screenshot');
      props.onScreenshotFailed?.();
      throw e;
    } finally {
      setProcessingScreenshot(false);
    }
  };

  const removeScreenshot = (): void => {
    setCurrentScreenshot(undefined);
    props.onScreenshotResultChanged(null);
  };

  return (
    <Stack horizontal>
      <Stack grow verticalFill verticalAlign="center" styles={screenshotFeedbackContainer(theme)}>
        {processingScreenshot ? (
          <Spinner aria-label="Taking screenshot" />
        ) : currentScreenshot ? (
          <FeedbackScreenshotPreview screenshot={currentScreenshot} onRemoveScreenshot={removeScreenshot} />
        ) : (
          <Stack.Item>
            <AddScreenshotButton onClick={onTakeScreenshot} />
          </Stack.Item>
        )}
      </Stack>
    </Stack>
  );
};

const screenshotFeedbackContainer = (theme: ITheme): IStackStyles => ({
  root: {
    background: theme.palette.neutralSecondary,
    padding: '2rem'
  }
});

const AddScreenshotButton = (props: { onClick: () => void }): JSX.Element => {
  return <DefaultButton onClick={props.onClick} text="Take Screenshot" />;
};

const FeedbackScreenshotPreview = (props: {
  screenshot: HTMLCanvasElement;
  onRemoveScreenshot: () => void;
}): JSX.Element => {
  useEffect(() => {
    document.getElementById('screenshot-holder-element')?.appendChild(props.screenshot);
  }, [props.screenshot]);

  return (
    <Stack styles={screenshotPreviewContainer}>
      <Stack.Item grow styles={{ root: { width: '100%' } }}>
        <Image src={props.screenshot.toDataURL()} alt="Screenshot preview" styles={screenshotImageStyles} />
      </Stack.Item>
      <DefaultButton text="Remove screenshot" onClick={props.onRemoveScreenshot} />
    </Stack>
  );
};

const screenshotPreviewContainer: IStackStyles = {
  root: {
    width: '12rem',
    background: '#121212'
  }
};

const screenshotImageStyles: Partial<IImageStyles> = {
  image: {
    width: '100%',
    objectFit: 'contain',
    maxHeight: '7rem'
  }
};
